import type { FC } from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import StudioDiscountIcon from '@wix/da-ds/pkg/Icons/24x24/StudioDiscount';
import EmptyDiscountList from '../EmptyDiscountList';
import SellerDiscountModal from '../SellerDiscountModal';
import {
  SELLER_DISCOUNTS_ACTIVE_STREAM,
  SELLER_DISCOUNTS_SCHEDULED_STREAM,
  SELLER_DISCOUNTS_ENDED_STREAM,
} from '../../../../../constants';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import DiscountStatusSection from './DiscountStatusSection';
import { StudioSection } from '../../../../types/store';

import s from './SellerDiscountsAll.scss';

interface Props {}

const SellerDiscountsAll: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { items: activeDiscounts } = useStream(SELLER_DISCOUNTS_ACTIVE_STREAM.streamId);
  const { items: scheduledDiscounts } = useStream(SELLER_DISCOUNTS_SCHEDULED_STREAM.streamId);
  const { items: endedDiscounts } = useStream(SELLER_DISCOUNTS_ENDED_STREAM.streamId);

  const isEmpty =
    activeDiscounts.length === 0 && scheduledDiscounts.length === 0 && endedDiscounts.length === 0;

  if (isEmpty) {
    return <EmptyDiscountList />;
  }

  const content = (
    <div className={s['root']}>
      <div className={s['create-discount-section']}>
        <div className={s['create-discount-caption']}>
          {t('studio.discounts.all.createCaption')}
        </div>
        <SolidButton
          size="medium"
          variant="main"
          priority="primary"
          icon={StudioDiscountIcon}
          onClick={() => setIsModalOpen(true)}
        >
          {t('studio.discounts.all.createButton')}
        </SolidButton>
      </div>
      {isModalOpen && <SellerDiscountModal onRequestClose={() => setIsModalOpen(false)} />}
      {activeDiscounts.length > 0 && (
        <DiscountStatusSection
          title={t('studio.discounts.status.active')}
          discounts={activeDiscounts as PapiSellerDiscount[]}
          statusSection={StudioSection.SELLER_DISCOUNTS_ACTIVE}
        />
      )}
      {scheduledDiscounts.length > 0 && (
        <DiscountStatusSection
          title={t('studio.discounts.status.scheduled')}
          discounts={scheduledDiscounts as PapiSellerDiscount[]}
          statusSection={StudioSection.SELLER_DISCOUNTS_SCHEDULED}
        />
      )}
      {endedDiscounts.length > 0 && (
        <DiscountStatusSection
          title={t('studio.discounts.status.ended')}
          discounts={endedDiscounts as PapiSellerDiscount[]}
          statusSection={StudioSection.SELLER_DISCOUNTS_ENDED}
        />
      )}
    </div>
  );

  if (isMobile) {
    return <ScrollableContent>{content}</ScrollableContent>;
  }

  return content;
};
SellerDiscountsAll.displayName = 'SellerDiscountsAll';

export default SellerDiscountsAll;
